// src/components/common/SimpleDatePicker.tsx

import React, { useState, useEffect } from 'react';
import { format } from 'date-fns';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { getNextMonday } from '../../utils/dateRangeUtils';
import { formatDateToString } from '../../utils/dateUtils';
import PickerShell from './PickerShell';

interface SimpleDatePickerProps {
  /** 當前選中的值 (YYYY-MM-DD 格式或 undefined) */
  currentValue?: string;

  /** 選擇回調 - 返回 YYYY-MM-DD 或空字串（清除） */
  onSelect: (value: string) => void;

  /** 關閉回調 */
  onClose: () => void;

  /** 最小日期限制 (YYYY-MM-DD 格式) */
  minDate?: string;

  /** 最大日期限制 (YYYY-MM-DD 格式) */
  maxDate?: string;

  /** 隱藏 "No date" 選項 */
  hideNoDate?: boolean;

  /** 自定義快捷選項（如果提供，將覆蓋預設選項） */
  customQuickOptions?: Array<{ value: string; label: string }>;
}

interface QuickOption {
  value: string;
  label: string;
  isNoDate?: boolean;
}

/**
 * 通用日期選擇器
 * 提供快捷選項和月曆視圖雙模式選擇
 * 用於 Plan 和 TargetDate 欄位
 */
const SimpleDatePicker: React.FC<SimpleDatePickerProps> = ({
  currentValue,
  onSelect,
  onClose,
  minDate,
  maxDate,
  hideNoDate = false,
  customQuickOptions
}) => {
  const [displayMonth, setDisplayMonth] = useState<Date>(() => {
    if (currentValue) return new Date(currentValue);
    return new Date();
  });

  const [selectedDate, setSelectedDate] = useState<string | undefined>(currentValue);

  useEffect(() => {
    setSelectedDate(currentValue);
  }, [currentValue]);

  const isDateInRange = (dateStr: string): boolean => {
    if (!dateStr) return true;
    const date = new Date(dateStr);
    if (minDate) {
      const min = new Date(minDate);
      min.setHours(0, 0, 0, 0);
      date.setHours(0, 0, 0, 0);
      if (date < min) return false;
    }
    if (maxDate) {
      const max = new Date(maxDate);
      max.setHours(0, 0, 0, 0);
      date.setHours(0, 0, 0, 0);
      if (date > max) return false;
    }
    return true;
  };

  const getQuickOptions = (): QuickOption[] => {
    if (customQuickOptions) {
      return customQuickOptions.map(opt => ({ value: opt.value, label: opt.label }));
    }

    const today = formatDateToString(new Date());
    const tomorrow = formatDateToString(new Date(Date.now() + 24 * 60 * 60 * 1000));
    const nextMonday = formatDateToString(getNextMonday());
    const now = new Date();
    const nextMonth = new Date(now.getFullYear(), now.getMonth() + 1, 1);
    const nextMonthStr = formatDateToString(nextMonth);

    const options: QuickOption[] = [
      { value: today, label: 'Today' },
      { value: tomorrow, label: 'Tomorrow' },
      { value: nextMonday, label: 'Next week' },
      { value: nextMonthStr, label: 'Next month' },
    ];

    if (!hideNoDate) {
      options.push({ value: '', label: 'No date', isNoDate: true });
    }

    return options;
  };

  const generateCalendarGrid = (): Date[] => {
    const year = displayMonth.getFullYear();
    const month = displayMonth.getMonth();
    const firstDayOfMonth = new Date(year, month, 1);
    const lastDayOfMonth = new Date(year, month + 1, 0);
    const firstDayWeekday = firstDayOfMonth.getDay();
    const daysFromPrevMonth = firstDayWeekday === 0 ? 6 : firstDayWeekday - 1;
    const daysInMonth = lastDayOfMonth.getDate();
    const totalCells = 42;
    const daysFromNextMonth = totalCells - daysFromPrevMonth - daysInMonth;

    const grid: Date[] = [];
    const prevMonthLastDay = new Date(year, month, 0).getDate();
    for (let i = prevMonthLastDay - daysFromPrevMonth + 1; i <= prevMonthLastDay; i++) {
      grid.push(new Date(year, month - 1, i));
    }
    for (let i = 1; i <= daysInMonth; i++) {
      grid.push(new Date(year, month, i));
    }
    for (let i = 1; i <= daysFromNextMonth; i++) {
      grid.push(new Date(year, month + 1, i));
    }
    return grid;
  };

  const handleQuickOptionClick = (value: string) => {
    onSelect(value);
    onClose();
  };

  const handleDateClick = (date: Date) => {
    const dateStr = formatDateToString(date);
    onSelect(dateStr);
    onClose();
  };

  const isToday = (date: Date): boolean => {
    const today = new Date();
    return (
      date.getDate() === today.getDate() &&
      date.getMonth() === today.getMonth() &&
      date.getFullYear() === today.getFullYear()
    );
  };

  const isSelected = (date: Date): boolean => {
    if (!selectedDate) return false;
    return formatDateToString(date) === selectedDate;
  };

  const isCurrentMonth = (date: Date): boolean => {
    return date.getMonth() === displayMonth.getMonth();
  };

  const quickOptions = getQuickOptions();
  const calendarGrid = generateCalendarGrid();
  const weekDays = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'];

  return (
    <PickerShell onClose={onClose}>
      <div className="max-h-[calc(90vh-56px)] flex flex-col overflow-hidden">
        {/* Quick Options */}
        <div className="px-4 pt-3 pb-2 border-b border-gray-100 space-y-0.5">
          {quickOptions.map((option) => {
            const isDisabled = !isDateInRange(option.value);
            return (
              <button
                key={option.value || 'no-date'}
                onClick={() => handleQuickOptionClick(option.value)}
                disabled={isDisabled}
                className={`
                  flex items-center justify-between w-full px-3 py-2 rounded-xl
                  transition-all border-2 border-transparent
                  ${isDisabled
                    ? 'text-gray-400 cursor-not-allowed bg-gray-50'
                    : option.isNoDate
                    ? 'text-gray-500 hover:bg-gray-50'
                    : 'text-gray-700 hover:bg-gray-50'
                  }
                `}
              >
                <span className="font-medium text-sm">{option.label}</span>
              </button>
            );
          })}
        </div>

        {/* Month Calendar */}
        <div className="px-4 py-2 overflow-y-auto">
          {/* Month Header */}
          <div className="flex items-center justify-between mb-1.5">
            <button
              onClick={() => setDisplayMonth(new Date(displayMonth.getFullYear(), displayMonth.getMonth() - 1, 1))}
              className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
              aria-label="Previous month"
            >
              <ChevronLeft className="w-4 h-4 text-gray-700" />
            </button>
            <h3 className="text-base font-semibold text-gray-900">
              {format(displayMonth, 'MMM yyyy')}
            </h3>
            <button
              onClick={() => setDisplayMonth(new Date(displayMonth.getFullYear(), displayMonth.getMonth() + 1, 1))}
              className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
              aria-label="Next month"
            >
              <ChevronRight className="w-4 h-4 text-gray-700" />
            </button>
          </div>

          {/* Week Days Header */}
          <div className="grid grid-cols-7 gap-0.5 mb-1">
            {weekDays.map((day) => (
              <div key={day} className="text-center text-xs font-medium text-gray-500 py-0.5">
                {day}
              </div>
            ))}
          </div>

          {/* Calendar Grid */}
          <div className="grid grid-cols-7 gap-0.5">
            {calendarGrid.map((date, index) => {
              const isTodayDate = isToday(date);
              const isSelectedDate = isSelected(date);
              const isCurrentMonthDate = isCurrentMonth(date);
              const dateStr = formatDateToString(date);
              const isDisabledDate = !isDateInRange(dateStr);

              return (
                <button
                  key={index}
                  onClick={() => handleDateClick(date)}
                  disabled={isDisabledDate}
                  className={`
                    aspect-square flex flex-col items-center justify-center rounded-lg text-sm
                    transition-colors relative
                    ${isDisabledDate
                      ? 'text-gray-300 cursor-not-allowed bg-gray-50'
                      : isSelectedDate
                      ? 'bg-sky-600 text-white font-semibold cursor-pointer'
                      : isCurrentMonthDate
                      ? 'text-gray-900 hover:bg-gray-100 cursor-pointer'
                      : 'text-gray-400 hover:bg-gray-50 cursor-pointer'
                    }
                  `}
                >
                  <span>{date.getDate()}</span>
                  {isTodayDate && !isSelectedDate && !isDisabledDate && (
                    <span className="absolute bottom-1 w-1 h-1 rounded-full bg-sky-600" />
                  )}
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </PickerShell>
  );
};

export default SimpleDatePicker;
